import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { shallowEqual } from 'react-redux';
import classNames from 'classnames';

import Button from '@alltrails/shared/denali/components/Button';
import Select from '@alltrails/shared/denali/components/Select';
import TextInput from '@alltrails/shared/denali/components/TextInput';
import TextArea from '@alltrails/shared/denali/components/TextArea';
import Checkbox from '@alltrails/shared/components/checkboxes/Checkbox24';
import DatePicker from 'components/shared/DatePicker';

import { alertTypeOptions, alertReasonOptions } from './constants';
import { adjustToSameNominalTimeForUser, adjustFromSameNominalTimeForUser } from '../../utils/alertsHelper';
import usePrevious from '../../hooks/usePrevious';
import { useSelector, useDispatch } from '../../redux/hooks';
import { Alert, AlertType } from '../../types/alerts';
import { AlgoliaArea, AreaId } from '../../types/areas';
import { createAlert, updateAlert, getAlert } from '../../redux/thunks/alerts';
import { AlertDrawerActionType, AlertDrawerActionTarget, setVisible, setPending } from '../../redux/slices/alertsDrawer/alertsDrawer';
import { setFilter, clearAllFiltersAndSetFilter } from '../../redux/slices/filters';
import * as styles from './styles/styles.module.scss';
import AlertsDrawerModal from './AlertsDrawerModal';
import AlertPickerItem from './AlertPickerItem';

const errorText = 'field is required';

const validateArea = (areaIdString: string, areas: Record<AreaId, AlgoliaArea>) => parseInt(areaIdString) in areas;
const validateName = (name: string) => !!name;
const validateDescription = (description: string) => !!description;
const validateAlertType = (alertType: string) => !!alertType;
const validateAlertReason = (alertReason: string, alertReasonEnabled: boolean) => !alertReasonEnabled || !!alertReason;
const validateStartDate = (startDate: Date, recursAnnually: boolean) => !recursAnnually || !!startDate;
const validateEndDate = (endDate: Date, startDate: Date, recursAnnually: boolean) =>
  (!recursAnnually || !!endDate) && (!startDate || !endDate || endDate >= startDate);
const getNominalDate = (date: string | null) => (date ? new Date(adjustToSameNominalTimeForUser(date)) : null);

const AlertsDrawer = (): JSX.Element => {
  const dispatch = useDispatch();
  const { visible, pending, saving, error, action, target, trailIds, alertId, alerts, alert, createdAlerts } = useSelector(
    ({ alertsDrawer }) => ({
      visible: alertsDrawer.visible,
      pending: alertsDrawer.pending,
      saving: alertsDrawer.saving,
      error: alertsDrawer.error,
      action: alertsDrawer.action,
      target: alertsDrawer.target,
      trailIds: alertsDrawer.trailIds,
      alertId: alertsDrawer.alertId,
      alerts: alertsDrawer.alerts,
      alert: alertsDrawer.alert,
      createdAlerts: alertsDrawer.createdAlerts
    }),
    shallowEqual
  );
  const filters = useSelector(state => state.filters.filters);
  const previousFilters = usePrevious(filters);
  const areas = useSelector(state => state.areas.areas);
  const areaOptions = Object.keys(areas).map(areaIdString => ({ label: areas[parseInt(areaIdString)].name, value: areaIdString }));

  const [filled, setFilled] = useState(false);
  const [alertsPickerVisible, setAlertsPickerVisible] = useState(false);
  const [areaIdString, setAreaIdString] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [alertType, setAlertType] = useState('');
  const [alertReason, setAlertReason] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [recursAnnually, setRecursAnnually] = useState(false);
  const alertReasonEnabled = false;
  const [modalVariant, setModalVariant] = useState(null);
  const previousModalVariant = usePrevious(modalVariant);

  const [areaError, setAreaError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [alertTypeError, setAlertTypeError] = useState(false);
  const [alertReasonError, setAlertReasonError] = useState(false);
  const [startDateError, setStartDateError] = useState(false);
  const [endDateError, setEndDateError] = useState(false);

  const closeDrawer = useCallback(() => {
    dispatch(setVisible({ visible: false }));

    // If the drawer is in edit mode and alertIds has been set, clear it
    if (action === AlertDrawerActionType.EDIT && filters?.alertIds?.length !== 0) {
      dispatch(setFilter({ filterKey: 'alertIds', value: [] }));
    }
  }, [dispatch, action, filters]);

  // If the drawer is open in edit mode and the alert is not yet loaded, load it and set the alertId filter
  useEffect(() => {
    if (visible && action === AlertDrawerActionType.EDIT && alertId && !alert) {
      dispatch(getAlert(alertId));
      dispatch(clearAllFiltersAndSetFilter({ filterKey: 'alertIds', value: [alertId] }));
    }
  }, [visible, action, alert, dispatch, alertId]);

  // If any global filters besides alertIds change while the drawer is open, close it
  useEffect(() => {
    if (
      visible &&
      previousFilters &&
      filters &&
      JSON.stringify(previousFilters.alertIds) === JSON.stringify(filters.alertIds) &&
      JSON.stringify(previousFilters) !== JSON.stringify(filters)
    ) {
      closeDrawer();
    }
  }, [visible, previousFilters, filters, closeDrawer]);

  // Initialize inputs in the drawer
  useEffect(() => {
    if (visible && !filled) {
      if (action === AlertDrawerActionType.EDIT && alert) {
        if (alert.objectId in areas) {
          setAreaIdString(areas[alert.objectId].ID.toString());
        }
        setName(alert.shortName);
        setDescription(alert.content);
        setAlertType(alert.alertType);
        setStartDate(getNominalDate(alert.startDate));
        setEndDate(getNominalDate(alert.endDate));
        setRecursAnnually(alert.recursAnnually);
        setFilled(true);
      } else {
        setName('');
        setDescription('');
        setStartDate(new Date());
        setEndDate(null);
        setRecursAnnually(false);

        if (action === AlertDrawerActionType.ADD) {
          setFilled(true);
        }
      }

      if (alerts && alerts.length !== 1 && !alert) {
        setAlertsPickerVisible(true);
      }
    } else if (!visible) {
      dispatch(setVisible({ alertId: null, visible }));
      setAlertsPickerVisible(false);
      setFilled(false);
    }
  }, [dispatch, action, alert, alerts, areas, visible, filled, alertsPickerVisible]);

  // Transition through modal variants as creating/saving an alert progresses
  useEffect(() => {
    if (saving && !modalVariant) {
      if (action === AlertDrawerActionType.ADD) {
        setModalVariant('creating-alert');
      } else {
        setModalVariant('saving-alert');
      }
    } else if (error && modalVariant && modalVariant !== 'error') {
      setModalVariant('error');
    } else if (!saving && !error && modalVariant) {
      if (action === AlertDrawerActionType.ADD) {
        if (target === AlertDrawerActionTarget.TRAILS) {
          setModalVariant('created-alert-button');
        } else {
          setModalVariant('created-alert');
        }
      } else {
        setModalVariant('saved-alert');
      }
    }
  }, [saving, error, action, target, modalVariant]);

  // Close the drawer when create/save has finished in a state that will show no buttons
  useEffect(() => {
    if (!modalVariant && ['created-alert', 'saved-alert'].includes(previousModalVariant)) {
      closeDrawer();
    }
  }, [previousModalVariant, modalVariant, closeDrawer]);

  if (!visible) {
    return null;
  }

  const title = action === AlertDrawerActionType.ADD ? 'Create a new alert' : 'Edit alert';

  const alertChanged = () => {
    if (!alert) {
      return false;
    }

    if (name !== alert.shortName || description !== alert.content) {
      return true;
    }

    if (alertType !== alert.alertType) {
      return true;
    }

    if (startDate?.getTime() !== getNominalDate(alert.startDate)?.getTime() || endDate?.getTime() !== getNominalDate(alert.endDate)?.getTime()) {
      return true;
    }

    if (recursAnnually !== alert.recursAnnually) {
      return true;
    }

    return false;
  };

  const saveDisabled = () => {
    if (action === AlertDrawerActionType.EDIT && !alertChanged()) {
      if (pending) {
        dispatch(setPending(false));
      }
      return true;
    }

    if (!pending) {
      dispatch(setPending(true));
    }

    return false;
  };

  const saveError = () => {
    let isError = false;
    let valid = false;

    if (target === AlertDrawerActionTarget.AREAS) {
      valid = validateArea(areaIdString, areas);
      setAreaError(!valid);
      isError = !valid || isError;
    }

    valid = validateName(name);
    setNameError(!valid);
    isError = !valid || isError;

    valid = validateDescription(description);
    setDescriptionError(!valid);
    isError = !valid || isError;

    valid = validateAlertType(alertType);
    setAlertTypeError(!valid);
    isError = !valid || isError;

    valid = validateAlertReason(alertReason, alertReasonEnabled);
    setAlertReasonError(!valid);
    isError = !valid || isError;

    valid = validateStartDate(startDate, recursAnnually);
    setStartDateError(!valid);
    isError = !valid || isError;

    valid = validateEndDate(endDate, startDate, recursAnnually);
    setEndDateError(!valid);
    isError = !valid || isError;

    if (action === AlertDrawerActionType.ADD && target === AlertDrawerActionTarget.TRAILS && trailIds.length === 0) {
      return true;
    }

    return isError;
  };

  const submitForm = async () => {
    // We're considering the date to be "timezone-less" so adjust it to be the same nominal value in UTC as in current admin's timezone
    const adjustedStartDate = startDate ? adjustFromSameNominalTimeForUser(startDate) : null;
    const adjustedEndDate = endDate ? adjustFromSameNominalTimeForUser(endDate) : null;
    const alertRequestPayload: Alert = {
      id: action === AlertDrawerActionType.EDIT && alert ? alert.id : null,
      alertType: alertType as AlertType,
      shortName: name,
      content: description,
      startDate: adjustedStartDate ? adjustedStartDate.toUTCString() : null,
      endDate: adjustedEndDate ? adjustedEndDate.toUTCString() : null,
      recursAnnually
    };
    if (action === AlertDrawerActionType.ADD && target === AlertDrawerActionTarget.TRAILS) {
      const trailsAlertRequestPayload = {
        trail_ids: trailIds,
        alert: alertRequestPayload
      };
      dispatch(createAlert(trailsAlertRequestPayload));
    } else if (action === AlertDrawerActionType.ADD && target === AlertDrawerActionTarget.AREAS) {
      const payload = {
        area_id: parseInt(areaIdString),
        alert: alertRequestPayload
      };
      // Note that this is creating an alert associated with all the trails in an area
      await dispatch(createAlert(payload));
    } else if (action === AlertDrawerActionType.EDIT && alert) {
      const trailsAlertRequestPayload = {
        areaId: alert.objectId,
        alert: alertRequestPayload
      };
      await dispatch(updateAlert(trailsAlertRequestPayload));
    }
  };

  const validateAndSubmit = () => {
    if (saveError()) {
      return;
    }

    submitForm();
  };

  const buttonLabel = action === AlertDrawerActionType.ADD ? 'Create alert' : 'Save alert';

  const renderPickerOrForm = () => {
    if (alertsPickerVisible) {
      return alerts.map(algoliaAlert => (
        <AlertPickerItem
          onEditClick={() => {
            dispatch(setVisible({ alertId: algoliaAlert.ID, visible: true, action, target }));
            setAlertsPickerVisible(false);
          }}
          key={algoliaAlert.ID}
          alert={algoliaAlert}
        />
      ));
    }

    return (
      <>
        {target === AlertDrawerActionTarget.AREAS && (
          <div className={styles.propertyContainer}>
            <h3 className={styles.contentTitle}>Property</h3>
            <Select
              labelText="Select Property"
              placeholder="Select Property"
              options={areaOptions}
              value={areaIdString}
              onChange={value => {
                setAreaIdString(value);
                setAreaError(!validateArea(value, areas));
              }}
              errorText={areaError ? errorText : ''}
              className={styles.input}
              size="md"
              testId="propertySelect"
              disabled={action === AlertDrawerActionType.EDIT}
              hideLabel
              required
            />
          </div>
        )}
        <h3 className={styles.contentTitle}>Alert details</h3>
        <TextInput
          placeholder="Name your alert"
          labelText="Name your alert"
          value={name}
          testId="nameTextInput"
          errorText={nameError ? errorText : ''}
          onChange={value => {
            setName(value);
            setNameError(!validateName(value));
          }}
          hideLabel
          required
        />
        <div className={styles.typeReason}>
          <div className={alertReasonEnabled ? classNames(styles.inputHalfContainer, styles.type) : styles.input}>
            <Select
              labelText="Alert type"
              placeholder="Alert type"
              options={alertTypeOptions}
              value={alertType}
              onChange={value => {
                setAlertType(value);
                setAlertTypeError(!validateAlertType(value));
              }}
              errorText={alertTypeError ? errorText : ''}
              className={styles.input}
              size="md"
              testId="alertTypeSelect"
              hideLabel
              required
            />
          </div>
          {alertReasonEnabled && (
            <div className={styles.inputHalfContainer}>
              <Select
                labelText="Alert reason"
                placeholder="Alert reason"
                value={alertReason}
                options={alertReasonOptions}
                onChange={value => {
                  setAlertReason(value);
                  setAlertReasonError(!validateAlertReason(value, alertReasonEnabled));
                }}
                errorText={alertReasonError ? errorText : ''}
                className={styles.input}
                size="md"
                testId="alertReasonSelect"
                hideLabel
                required
              />
            </div>
          )}
        </div>
        <div className={styles.descriptionContainer}>
          <TextArea
            labelText="Detailed alert description"
            placeholder="Detailed alert description"
            testId="descriptionTextArea"
            onChange={value => {
              setDescription(value);
              setDescriptionError(!validateDescription(value));
            }}
            value={description}
            errorText={descriptionError ? errorText : ''}
            rows={5}
            hideLabel
            required
          />
        </div>
        <div className={styles.dateLabelsContainer}>
          <span className={classNames(styles.dateLabel, styles.startDate)}>Start date</span>
          <span className={styles.dateLabel}>End date</span>
        </div>
        <div className={styles.datesContainer}>
          <div className={classNames(styles.inputHalfContainer, styles.startDate)}>
            <DatePicker
              materialRootClass={startDateError && styles.error}
              date={startDate}
              onChange={(date: Date) => {
                setStartDate(date);
                setStartDateError(!validateStartDate(date, recursAnnually));
              }}
              abbreviateMonth
              disableFuture={false}
            />
          </div>
          <div className={styles.inputHalfContainer}>
            <DatePicker
              materialRootClass={endDateError && styles.error}
              date={endDate}
              onChange={(date: Date) => {
                setEndDate(date);
                setEndDateError(!validateEndDate(date, startDate, recursAnnually));
              }}
              abbreviateMonth
              disablePast
              disableFuture={false}
            />
          </div>
        </div>
        <div className={styles.annualToggleContainer}>
          <span className={styles.annualToggleLabel}>Recurs annually</span>
          <Checkbox
            id="recurs_annually"
            name="recurs_annually"
            value=""
            checked={recursAnnually}
            onChange={() => setRecursAnnually(!recursAnnually)}
          />
        </div>
      </>
    );
  };

  return (
    <div className={styles.container}>
      {modalVariant &&
        createPortal(
          <AlertsDrawerModal
            variant={modalVariant}
            setVariant={setModalVariant}
            closeModal={() => {
              closeDrawer();
            }}
            isMounted={!!modalVariant}
            onClick={() => {
              setModalVariant(null);
              dispatch(setVisible({ visible: true, action: AlertDrawerActionType.EDIT, target, alerts: createdAlerts }));
            }}
          />,
          document.getElementById('modalPortal')
        )}
      <div className={styles.innerContainer}>
        <div className={styles.top}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>Update your visitors about timely information regarding your trails, parking lots etc.</p>
        </div>
        <div className={styles.content}>{renderPickerOrForm()}</div>
        <div className={styles.bottom}>
          <Button
            text={buttonLabel}
            disabled={saveDisabled()}
            onClick={() => validateAndSubmit()}
            testId={buttonLabel}
            variant="primary"
            width="full"
          />
        </div>
      </div>
    </div>
  );
};

export default AlertsDrawer;
